import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { openAlert, openConfirm } from '../../utils/common';
import Spinner from '../UI/Spinner';
import passwordPolicyService from '../../services/passwordPolicyService';
import { sanitizeInput, validateNumericInput } from '../../utils/xssProtection';

/**
 * 비밀번호 정책 설정 컴포넌트
 * 비밀번호 유효기간을 관리자/일반 사용자별로 설정할 수 있는 컴포넌트
 */
const PasswordPolicySettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [policies, setPolicies] = useState({
    admin_days: {value: 90, description: ''}, // 기본값: 관리자 90일
    user_days: {value: 180, description: ''}  // 기본값: 일반 사용자 180일
  });
  
  const [formValues, setFormValues] = useState({
    adminDays: 90,
    userDays: 180
  });

  const [validationErrors, setValidationErrors] = useState({
    adminDays: null,
    userDays: null
  });

  // 정책 정보 가져오기
  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await passwordPolicyService.getPolicies();
      setPolicies(result);
      setFormValues({
        adminDays: result.admin_days?.value || 90,
        userDays: result.user_days?.value || 180
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '정책 정보를 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      openAlert({ title: '알림', description: errorMessage });
    } finally {
      setLoading(false);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // 실시간 입력값 검증
  const validateField = useCallback((fieldName, value) => {
    const numericValue = Number(value);
    
    if (numericValue < 30) {
      return '최소 30일 이상이어야 합니다.';
    }
    if (numericValue > 365) {
      return '최대 365일까지 설정 가능합니다.';
    }
    if (numericValue === 0) {
      return '유효한 값을 입력해주세요.';
    }
    
    return null;
  }, []);

  // 입력값 변경 처리 - XSS 방지 및 실시간 검증 추가
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // XSS 방지를 위한 입력값 정화
    const sanitizedValue = sanitizeInput(value);
    
    // 숫자만 허용하는 검증
    const validationResult = validateNumericInput(sanitizedValue, {
      min: 0,
      max: 999,
      allowEmpty: false
    });
    
    if (!validationResult.isValid) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: validationResult.error
      }));
      return;
    }
    
    let numericValue = sanitizedValue.replace(/[^0-9]/g, '');
    
    // 비어있으면 0으로 설정
    if (!numericValue) numericValue = '0';
    
    // 실시간 필드 검증
    const fieldError = validateField(name, numericValue);
    setValidationErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
    
    setFormValues({
      ...formValues,
      [name]: numericValue
    });
  };

  // 정책 저장
  const handleSave = async () => {
    // 실시간 검증 오류가 있는지 확인
    if (validationErrors.adminDays || validationErrors.userDays) {
      openAlert({ title: '알림', description: '입력값을 확인해주세요.' });
      return;
    }

    // 추가 유효성 검증 (이중 검증)
    const adminDays = Number(formValues.adminDays);
    const userDays = Number(formValues.userDays);
    
    // XSS 방지를 위한 추가 검증
    if (!Number.isInteger(adminDays) || !Number.isInteger(userDays)) {
      openAlert({ title: '알림', description: '올바른 숫자를 입력해주세요.' });
      return;
    }
    
    if (adminDays < 30 || adminDays > 365) {
      openAlert({ title: '알림', description: '관리자 비밀번호 유효기간은 30일에서 365일 사이여야 합니다.' });
      return;
    }
    
    if (userDays < 30 || userDays > 365) {
      openAlert({ title: '알림', description: '일반 사용자 비밀번호 유효기간은 30일에서 365일 사이여야 합니다.' });
      return;
    }

    openConfirm({
      title: '비밀번호 정책 변경',
      description: '비밀번호 정책을 변경하시겠습니까?',
      positive_method: async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
          const result = await passwordPolicyService.updatePolicies(adminDays, userDays);
          //openAlert({ title: '알림', description: result.message });
          setSuccess(result.message);
          
          // 정책 정보 갱신
          setPolicies({
            admin_days: {value: adminDays, description: ''},
            user_days: {value: userDays, description: ''}
          });
        } catch (error) {
          setError(error.message || '정책 변경 중 오류가 발생했습니다.');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  // 변경 사항이 있는지 확인 및 저장 버튼 활성화 조건
  const hasChanges = policies.admin_days.value !== Number(formValues.adminDays) || policies.user_days.value !== Number(formValues.userDays);
  const hasValidationErrors = validationErrors.adminDays || validationErrors.userDays;
  const canSave = hasChanges && !hasValidationErrors && !loading;

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">비밀번호 유효기간 설정</h5>
      </Card.Header>
      <Card.Body>
        <Spinner visible={loading} />
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <div className="form_info_box mb-4">
          <p className="sub_desc">역할별 비밀번호 유효기간을 설정합니다. 기간은 30일에서 365일 사이로 설정할 수 있습니다.</p>
        </div>

        <Form>
          <Form.Group className="mb-3" controlId="adminPassword">
            <Form.Label>관리자 비밀번호 유효기간</Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                type="number"
                className={`w-25 ${validationErrors.adminDays ? 'is-invalid' : ''}`}
                name="adminDays"
                value={formValues.adminDays}
                onChange={handleChange}
                min="30"
                max="365"
                placeholder="30-365"
              />
              <span className="ms-2">일</span>
            </div>
            {validationErrors.adminDays && (
              <div className="invalid-feedback d-block">
                {validationErrors.adminDays}
              </div>
            )}
            <Form.Text className="text-muted">
              관리자 비밀번호 유효기간(권장: 90일, 범위: 30-365일)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="userPassword">
            <Form.Label>일반 사용자 비밀번호 유효기간</Form.Label>
            <div className="d-flex align-items-center">
              <Form.Control
                type="number"
                className={`w-25 ${validationErrors.userDays ? 'is-invalid' : ''}`}
                name="userDays"
                value={formValues.userDays}
                onChange={handleChange}
                min="30"
                max="365"
                placeholder="30-365"
              />
              <span className="ms-2">일</span>
            </div>
            {validationErrors.userDays && (
              <div className="invalid-feedback d-block">
                {validationErrors.userDays}
              </div>
            )}
            <Form.Text className="text-muted">
              일반 사용자 비밀번호 유효기간(권장: 180일, 범위: 30-365일)
            </Form.Text>
          </Form.Group>
        </Form>
      </Card.Body>
      <Card.Footer className="d-flex justify-content-end">
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? '저장 중...' : '설정 저장'}
        </Button>
      </Card.Footer>
    </Card>
  );
};

export default PasswordPolicySettings;
